import type { FlatConfig, AfaNodeModel } from './flatConfig';

export interface StorageBlock {
  enabled: boolean;
  minRawCapacity: number;
  nodeCount: number;
  vpodSsdCapacity: number;
  encodingScheme: string;
  spares: number;
  compressionRatio: number;
  endToEndEncryption: boolean;
  // Stamped by useConfigurationLogic when capacity/vendor mappings resolve;
  // absent on a fresh config.
  ssdPartNumber?: string;
}

export interface HybridStorageBlock extends StorageBlock {
  jbodSize: number;
  hddCapacity: number;
  mlec: boolean;
  hddPartNumber?: string; // stamped dynamically, like ssdPartNumber
}

// CONF-210. Only the flash set has a node-model choice; hybrid S-nodes keep
// their single SKU, which is why this is not on the shared StorageBlock.
export interface AfaStorageBlock extends StorageBlock {
  nodeModel?: AfaNodeModel; // absent = 'standard'; see flatConfig.ts
}

export function hybridBlockFromFlat(c: FlatConfig): HybridStorageBlock {
  return {
    enabled: c.hybridEnabled,
    minRawCapacity: c.hybridMinRawCapacity,
    nodeCount: c.hybridNodeCount,
    vpodSsdCapacity: c.hybridVpodSsdCapacity,
    encodingScheme: c.hybridEncodingScheme,
    spares: c.hybridSpares,
    compressionRatio: c.hybridCompressionRatio,
    endToEndEncryption: c.hybridEndToEndEncryption,
    jbodSize: c.jbodSize,
    hddCapacity: c.hybridHddCapacity,
    mlec: c.hybridMLEC,
    ...(c.hybridSsdPartNumber !== undefined ? { ssdPartNumber: c.hybridSsdPartNumber } : {}),
    ...(c.hybridHddPartNumber !== undefined ? { hddPartNumber: c.hybridHddPartNumber } : {}),
  };
}

// Precise key set this mapper owns — return a Pick (not Partial<FlatConfig>) so
// the compiler can verify total FlatConfig coverage when boxes are composed.
export type HybridFlatFields = Pick<
  FlatConfig,
  | 'hybridEnabled'
  | 'hybridMinRawCapacity'
  | 'hybridNodeCount'
  | 'hybridVpodSsdCapacity'
  | 'hybridEncodingScheme'
  | 'hybridSpares'
  | 'hybridCompressionRatio'
  | 'hybridEndToEndEncryption'
  | 'jbodSize'
  | 'hybridHddCapacity'
  | 'hybridMLEC'
  | 'hybridSsdPartNumber'
  | 'hybridHddPartNumber'
>;

export function hybridBlockToFlat(b: HybridStorageBlock): HybridFlatFields {
  return {
    hybridEnabled: b.enabled,
    hybridMinRawCapacity: b.minRawCapacity,
    hybridNodeCount: b.nodeCount,
    hybridVpodSsdCapacity: b.vpodSsdCapacity,
    hybridEncodingScheme: b.encodingScheme,
    hybridSpares: b.spares,
    hybridCompressionRatio: b.compressionRatio,
    hybridEndToEndEncryption: b.endToEndEncryption,
    jbodSize: b.jbodSize,
    hybridHddCapacity: b.hddCapacity,
    hybridMLEC: b.mlec,
    ...(b.ssdPartNumber !== undefined ? { hybridSsdPartNumber: b.ssdPartNumber } : {}),
    ...(b.hddPartNumber !== undefined ? { hybridHddPartNumber: b.hddPartNumber } : {}),
  };
}

export function afaBlockFromFlat(c: FlatConfig): AfaStorageBlock {
  return {
    enabled: c.afaEnabled,
    minRawCapacity: c.afaMinRawCapacity,
    nodeCount: c.afaNodeCount,
    vpodSsdCapacity: c.afaVpodSsdCapacity,
    encodingScheme: c.afaEncodingScheme,
    spares: c.afaSpares,
    compressionRatio: c.afaCompressionRatio,
    endToEndEncryption: c.afaEndToEndEncryption,
    ...(c.afaSsdPartNumber !== undefined ? { ssdPartNumber: c.afaSsdPartNumber } : {}),
    // Loose `!= null` on purpose: a configV2 row saved before CONF-210 comes
    // back from AppSync with nodeModel: null, which must read as "absent".
    ...(c.afaNodeModel != null ? { nodeModel: c.afaNodeModel } : {}),
  };
}

export type AfaFlatFields = Pick<
  FlatConfig,
  | 'afaEnabled'
  | 'afaMinRawCapacity'
  | 'afaNodeCount'
  | 'afaVpodSsdCapacity'
  | 'afaEncodingScheme'
  | 'afaSpares'
  | 'afaCompressionRatio'
  | 'afaEndToEndEncryption'
  | 'afaSsdPartNumber'
  | 'afaNodeModel'
>;

export function afaBlockToFlat(b: AfaStorageBlock): AfaFlatFields {
  return {
    afaEnabled: b.enabled,
    afaMinRawCapacity: b.minRawCapacity,
    afaNodeCount: b.nodeCount,
    afaVpodSsdCapacity: b.vpodSsdCapacity,
    afaEncodingScheme: b.encodingScheme,
    afaSpares: b.spares,
    afaCompressionRatio: b.compressionRatio,
    afaEndToEndEncryption: b.endToEndEncryption,
    ...(b.ssdPartNumber !== undefined ? { afaSsdPartNumber: b.ssdPartNumber } : {}),
    ...(b.nodeModel != null ? { afaNodeModel: b.nodeModel } : {}),
  };
}
